import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/projects/NPS/Stepper/common/button";
const NPSLogo = "/assets/NPSLogo.png";
const userIcon = "/assets/user.svg";
import CommonModal from "@/projects/NPS/Stepper/common/Modal";
import OtpVerification from "../OtpVerification";
import { getAccessToken } from "@/lib/storeManager";
import { useNavigate } from "react-router-dom";
import NavigationRoutes from "@/projects/NPS/Stepper/utils/navigationRoutes";

export default function Header() {
  const navigate = useNavigate();
  const isLoggedIn = getAccessToken() !== null;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const getMobile = localStorage.getItem("userMobile");

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };
    if (isMobileMenuOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  const handleLoginSignup = () => {
    setIsMobileMenuOpen(false);
    setIsOtpOpen(true);
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsLogoutOpen(false);
    window.location.reload();
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-white px-4 py-1 shadow-sm md:py-0 lg:px-10">
      <div className="container mx-auto w-full">
        <div className="flex h-16 items-center justify-between sm:h-20 lg:h-20">
          {/* Logo */}
          <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4">
            <img className="w-full" src={NPSLogo} alt="Fincare Logo" />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden items-center space-x-4 md:flex lg:space-x-8">
            <div
              className="cursor-pointer font-nunito text-[18px] font-extrabold leading-loose text-[#1DAB91]"
              onClick={() => navigate(NavigationRoutes.HOME)}
            >
              NPS
            </div>
            <div className="flex items-center space-x-2 lg:space-x-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E4FFFB] p-2 lg:h-12 lg:w-12 lg:p-3">
                <img
                  className="h-5 w-5 text-[#266985] lg:h-6 lg:w-5"
                  src={userIcon}
                  alt="User Icon"
                />
              </div>
              {!isLoggedIn ? (
                <button
                  type="button"
                  className="font-nunito text-base font-bold text-[#707070] transition-colors hover:text-[#266985]"
                  onClick={handleLoginSignup}
                >
                  Login / Sign up
                </button>
              ) : (
                <button
                  type="button"
                  className="font-nunito text-base font-bold text-[#707070] transition-colors hover:text-[#266985]"
                  onClick={() => setIsLogoutOpen(!isLogoutOpen)}
                >
                  {getMobile}
                </button>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="bg-[#E4FFFB] p-2 md:hidden"
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? (
              <X className="h-10 w-10 text-[#266985]" />
            ) : (
              <Menu className="h-10 w-10 text-[#266985]" />
            )}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="border-t border-slate-200 bg-white md:hidden">
            <div className="space-y-2 px-3 pb-3 pt-3 sm:px-4 sm:pb-4 sm:pt-4">
              <div className="flex items-center justify-center border-b border-slate-100 pb-3">
                <div
                  className="cursor-pointer font-nunito text-lg font-extrabold text-[#1DAB91]"
                  onClick={() => navigate(NavigationRoutes.HOME)}
                >
                  NPS
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3 py-2">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E4FFFB] p-2">
                  <img
                    className="h-5 w-5 text-slate-600"
                    src={userIcon}
                    alt="User Icon"
                  />
                </div>
                {!isLoggedIn ? (
                  <button
                    type="button"
                    className="font-nunito text-base font-bold text-[#707070] transition-colors hover:text-[#266985]"
                    onClick={handleLoginSignup}
                  >
                    Login / Sign up
                  </button>
                ) : (
                  <button
                    type="button"
                    className="font-nunito text-base font-bold text-[#707070] transition-colors hover:text-[#266985]"
                    onClick={() => setIsLogoutOpen(true)}
                  >
                    {getMobile}
                  </button>
                )}
              </div>
            </div>
          </nav>
        )}
      </div>

      {/* OTP Modal */}
      <CommonModal
        isOpen={isOtpOpen}
        onClose={() => setIsOtpOpen(false)}
        width="w-full sm:w-[600px]"
        height="h-auto"
        padding="p-2 sm:p-6"
      >
        <OtpVerification
          onClose={() => setIsOtpOpen(false)}
          onSuccess={() => {
            setIsOtpOpen(false);
            window.location.reload();
          }}
        />
      </CommonModal>

      {/* Mobile Logout Modal with Animation */}
      <div
        className={`fixed inset-0 z-50 flex items-end justify-center bg-[#00000080] backdrop-blur-sm transition-all duration-300 md:hidden ${
          isLogoutOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
        onClick={() => setIsLogoutOpen(false)}
      >
        <div
          className={`mx-4 mb-4 w-full transform rounded-t-2xl bg-[#E4FFFB] shadow-xl transition-all duration-300 ${
            isLogoutOpen ? "translate-y-0" : "translate-y-full"
          }`}
          onClick={e => e.stopPropagation()}
        >
          <div className="space-y-4 p-6 text-center">
            <h2 className="font-nunito text-lg font-bold text-[#266985]">
              Are you sure you want to log out?
            </h2>
            <div className="mt-4 flex justify-center space-x-4">
              <button
                onClick={() => setIsLogoutOpen(false)}
                className="rounded-md border border-[#266985] px-5 py-2 font-nunito font-semibold text-[#266985] transition hover:bg-[#d3f5f0]"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="rounded-md bg-gradient-to-l from-[#1AAE90] to-[#258996] px-5 py-2 font-nunito font-semibold text-white transition hover:bg-[#1f5870]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Logout Modal - Right Side with Animation */}
      <div
        className={`fixed right-0 z-50 hidden transition-all duration-300 md:block ${
          isLogoutOpen
            ? "top-28 opacity-100"
            : "pointer-events-none -top-full opacity-0"
        }`}
      >
        <div
          className={`mr-4 w-[400px] transform rounded-lg bg-[#E4FFFB] shadow-xl transition-all duration-300 md:mr-6 lg:mr-10 ${
            isLogoutOpen
              ? "translate-y-0 opacity-100"
              : "-translate-y-4 opacity-0"
          }`}
          onClick={e => e.stopPropagation()}
        >
          <div className="space-y-4 p-6 text-center">
            <h2 className="font-nunito text-lg font-bold text-[#266985]">
              Are you sure you want to log out?
            </h2>
            <div className="mt-4 flex justify-center space-x-4">
              <button
                onClick={() => setIsLogoutOpen(false)}
                className="rounded-md border border-[#266985] px-5 py-2 font-nunito font-semibold text-[#266985] transition hover:bg-[#d3f5f0]"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="rounded-md bg-gradient-to-l from-[#1AAE90] to-[#258996] px-5 py-2 font-nunito font-semibold text-white transition hover:bg-[#1f5870]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdown - Desktop only */}
      {isLogoutOpen && (
        <div
          className="fixed inset-0 z-40 hidden bg-[#00000080] backdrop-blur-sm transition-all duration-300 md:block"
          onClick={() => setIsLogoutOpen(false)}
        />
      )}
    </header>
  );
}
